import React, { useCallback, useMemo, useRef } from 'react';

import { Button } from '@appcharge/shared-ui';
import usePermissions from 'api/usePermissions';
import { useUserDetails } from 'api/useUserDetails';
import dayjs from 'dayjs';
import { formatDateByLocale } from 'utils/formattingUtil';
import { permissionsUtil } from 'utils/permissionsUtil';
import { getSelectProjectOptions } from 'utils/projectsUtil';

import { FilterPopover } from 'components/FilterComponents/FilterPopover/FilterPopover';
import { FilterType } from 'components/FilterComponents/FilterPopover/types';

interface ReportsFilterBarProps {
    clearFiltersButton: boolean;
    handleClearFilters: () => void;
    selectedProjects: string[];
    onProjectChange: (value: string) => void;
    startDate: string | undefined;
    endDate: string | undefined;
    onDateChange: (startDate: string, endDate: string) => void;
}

const ReportsFilterBarComponent: React.FC<ReportsFilterBarProps> = ({
    clearFiltersButton,
    handleClearFilters,
    selectedProjects,
    onProjectChange,
    startDate,
    endDate,
    onDateChange
}) => {
    const { getPermissions } = usePermissions();
    const permissions = getPermissions();
    const allowedPublishers = useMemo(
        () => permissionsUtil.getPublishersByFinancialReports(permissions),
        [permissions]
    );

    const { userDetails } = useUserDetails();
    const projectsDetails = useMemo(() => {
        return userDetails?.projects;
    }, [userDetails]);

    const projectOptions = useMemo(() => {
        return projectsDetails && allowedPublishers.length > 0
            ? getSelectProjectOptions(projectsDetails, allowedPublishers).map((project) => ({
                label: project.content as string,
                value: project.value
            }))
            : [];
    }, [projectsDetails, allowedPublishers]);

    // Date filter display and value
    const currentDateValue = useMemo(() => {
        if (startDate && endDate) {
            return JSON.stringify({
                startDate,
                endDate
            });
        }
        return '';
    }, [startDate, endDate]);

    const { displayLabel, displayParts } = useMemo(() => {
        if (startDate && endDate) {
            const prefix = 'Pick a date';
            const extractDate = (dateStr: string) => {
                return dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
            };

            const start = dayjs(extractDate(startDate));
            const end = dayjs(extractDate(endDate));

            if (start.isSame(end, 'day')) {
                const dateStr = formatDateByLocale(start.toISOString());
                return {
                    displayLabel: `${prefix} | ${dateStr}`,
                    displayParts: [prefix, dateStr]
                };
            }
            const dateRange = `${formatDateByLocale(start.toISOString())} - ${formatDateByLocale(end.toISOString())}`;
            return {
                displayLabel: `${prefix} | ${dateRange}`,
                displayParts: [prefix, dateRange]
            };
        }
        return {
            displayLabel: 'Pick a date',
            displayParts: undefined
        };
    }, [startDate, endDate]);

    const onDateChangeRef = useRef(onDateChange);
    onDateChangeRef.current = onDateChange;

    const handleDateChange = useCallback((value: string) => {
        if (!value) {
            onDateChangeRef.current('', '');
            return;
        }

        try {
            const parsed = JSON.parse(value);
            if (parsed.startDate && parsed.endDate) {
                onDateChangeRef.current(parsed.startDate, parsed.endDate);
            }
        } catch (error) {
            console.error('Failed to parse date range:', error);
        }
    }, []);

    return (
        <div className="flex flex-row gap-1 items-center flex-wrap">
            <FilterPopover
                label="Project"
                filterType={FilterType.AUTOCOMPLETE_CHECKLIST}
                options={projectOptions}
                selectedItems={selectedProjects}
                onChange={onProjectChange}
                placeholder="Search projects..."
                dataTestId="reportsProjectFilter"
            />
            <FilterPopover
                label={displayLabel}
                labelParts={displayParts}
                onChange={handleDateChange}
                selectedItems={currentDateValue ? [currentDateValue] : []}
                filterType={FilterType.DATE_PICKER}
                hideIndicator
                dataTestId="reportsDateFilter"
            />
            {clearFiltersButton && (
                <Button
                    variant="link"
                    onClick={handleClearFilters}
                    className="text-indigo-600 hover:text-base-primary text-xs border-none shadow-none"
                >
                    Clear filters
                </Button>
            )}
        </div>
    );
};

export const ReportsFilterBar = React.memo(ReportsFilterBarComponent);

ReportsFilterBar.displayName = 'ReportsFilterBar';

